import { useState } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useStaffAuth } from '@/contexts/StaffAuthContext';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import {
  LayoutDashboard,
  Users,
  Repeat,
  QrCode,
  Receipt,
  Package,
  Wallet,
  Bell,
  BarChart3,
  UserCog,
  CalendarClock,
  ListChecks,
  Clock,
  Briefcase,
  ChevronDown,
  ChevronRight,
  Settings as SettingsIcon,
  LogOut,
  HandCoins,
  Menu,
  X,
} from 'lucide-react';

const NAV = [
  { to: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: null },
  { to: '/admin/customers', label: 'Customers', icon: Users, roles: null },
  { to: '/admin/subscriptions', label: 'Subscriptions', icon: Repeat, roles: null },
  { to: '/admin/pending-payments', label: 'Pending Payments', icon: HandCoins, roles: null },
  { to: '/admin/scanner', label: 'QR Scanner', icon: QrCode, roles: null },
  { to: '/admin/redemptions', label: 'Redemptions', icon: Receipt, roles: null },
  { to: '/admin/plans', label: 'Plans & Add-ons', icon: Package, roles: ['ADMIN', 'MANAGER'] },
  { to: '/admin/payments', label: 'Payments', icon: Wallet, roles: null },
  { to: '/admin/notifications', label: 'Notifications', icon: Bell, roles: ['ADMIN', 'MANAGER'] },
  { to: '/admin/reports', label: 'Reports', icon: BarChart3, roles: ['ADMIN', 'MANAGER'] },
  {
    group: 'HR',
    label: 'HR',
    icon: Briefcase,
    roles: ['ADMIN', 'MANAGER'],
    children: [
      { to: '/admin/staff', label: 'Staff', icon: UserCog, roles: ['ADMIN', 'MANAGER'] },
      { to: '/admin/scheduling', label: 'Scheduling', icon: CalendarClock, roles: ['ADMIN', 'MANAGER'] },
      { to: '/admin/timesheet', label: 'Timesheet', icon: Clock, roles: ['ADMIN', 'MANAGER'] },
      { to: '/admin/checklists', label: 'Checklists', icon: ListChecks, roles: ['ADMIN', 'MANAGER'] },
    ],
  },
  { to: '/admin/settings', label: 'Settings', icon: SettingsIcon, roles: ['ADMIN'] },
];

export default function AdminLayout() {
  const { staff, logout } = useStaffAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const roleOk = (item) => !item.roles || item.roles.includes(staff?.role);
  const visibleNav = NAV.filter(roleOk).map((item) => (
    item.children ? { ...item, children: item.children.filter(roleOk) } : item
  )).filter((item) => !item.children || item.children.length);

  const isChildActive = (children) => children.some((c) => location.pathname === c.to || location.pathname.startsWith(`${c.to}/`));
  const [openGroups, setOpenGroups] = useState(() => {
    const init = {};
    for (const item of NAV) if (item.children) init[item.label] = isChildActive(item.children);
    return init;
  });

  const doLogout = () => {
    logout();
    navigate('/staff/login');
  };

  const linkClass = (extra = '') => ({ isActive }) =>
    `flex items-center gap-3 px-5 py-2.5 text-sm font-medium transition-colors duration-150 border-l-2 ${extra} ${
      isActive ? 'bg-white/10 text-white border-saffron' : 'text-white/60 border-transparent hover:text-white hover:bg-white/5'
    }`;

  const SidebarInner = ({ onNavigate }) => (
    <>
      <div className="px-5 py-6 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/logo-emblem.png" alt="The Tiffin" className="w-10 h-10 rounded-full object-cover shrink-0" />
          <div>
            <p className="font-heading text-base font-700 tracking-tight leading-tight">THE TIFFIN</p>
            <p className="text-[11px] text-white/40 mt-0.5">Counter &amp; Management</p>
          </div>
        </div>
        <button
          data-testid="admin-mobile-nav-close"
          onClick={() => setMobileNavOpen(false)}
          className="lg:hidden text-white/60 hover:text-white"
        >
          <X size={20} />
        </button>
      </div>
      <nav className="flex-1 overflow-y-auto py-3 scrollbar-none">
        {visibleNav.map((item) => {
          if (item.children) {
            const Icon = item.icon;
            const open = openGroups[item.label];
            const active = isChildActive(item.children);
            return (
              <div key={item.label}>
                <button
                  data-testid={`sidebar-group-${item.label.toLowerCase()}`}
                  onClick={() => setOpenGroups((g) => ({ ...g, [item.label]: !g[item.label] }))}
                  className={`w-full flex items-center gap-3 px-5 py-2.5 text-sm font-medium transition-colors duration-150 border-l-2 ${
                    active ? 'bg-white/5 text-white border-saffron' : 'text-white/60 border-transparent hover:text-white hover:bg-white/5'
                  }`}
                >
                  <Icon size={17} strokeWidth={2} />
                  {item.label}
                  {open ? <ChevronDown size={15} className="ml-auto" /> : <ChevronRight size={15} className="ml-auto" />}
                </button>
                {open && item.children.map(({ to, label, icon: ChildIcon }) => (
                  <NavLink
                    key={to}
                    to={to}
                    onClick={onNavigate}
                    data-testid={`sidebar-link-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                    className={linkClass('pl-12')}
                  >
                    <ChildIcon size={15} strokeWidth={2} />
                    {label}
                  </NavLink>
                ))}
              </div>
            );
          }
          const { to, label, icon: Icon } = item;
          return (
            <NavLink
              key={to}
              to={to}
              onClick={onNavigate}
              data-testid={`sidebar-link-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
              className={linkClass()}
            >
              <Icon size={17} strokeWidth={2} />
              {label}
            </NavLink>
          );
        })}
      </nav>
      <div className="px-5 py-4 border-t border-white/10">
        <p className="text-sm font-medium truncate" data-testid="sidebar-staff-name">
          {staff?.name}
        </p>
        <p className="text-[11px] text-white/40 mb-3">{staff?.role}</p>
        <button
          data-testid="staff-logout-btn"
          onClick={doLogout}
          className="flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors duration-150"
        >
          <LogOut size={15} /> Log out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-bone">
      <div className="lg:hidden sticky top-0 z-20 bg-obsidian text-white flex items-center justify-between px-4 py-3">
        <button data-testid="admin-mobile-nav-open" onClick={() => setMobileNavOpen(true)} aria-label="Open menu">
          <Menu size={22} />
        </button>
        <div className="flex items-center gap-2">
          <img src="/logo-emblem.png" alt="The Tiffin" className="w-7 h-7 rounded-full object-cover" />
          <p className="font-heading text-base font-700 tracking-tight">THE TIFFIN</p>
        </div>
        <span className="w-[22px]" />
      </div>

      {mobileNavOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="w-64 h-full bg-obsidian text-white flex flex-col" data-testid="admin-mobile-sidebar">
            <SidebarInner onNavigate={() => setMobileNavOpen(false)} />
          </div>
          <div
            className="flex-1 bg-black/50"
            onClick={() => setMobileNavOpen(false)}
            data-testid="admin-mobile-nav-backdrop"
          />
        </div>
      )}

      <aside className="hidden lg:flex w-60 shrink-0 bg-obsidian text-white flex-col fixed inset-y-0 left-0 z-30">
        <SidebarInner onNavigate={() => {}} />
      </aside>

      <main className="lg:ml-60 min-h-screen">
        <div className="p-4 sm:p-6 lg:p-8">
          <ErrorBoundary key={window.location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </div>
      </main>
    </div>
  );
}
